import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { ArrowLeft, ChevronLeft, ChevronRight, Palette, X } from "lucide-react";

import { TEMPLATE_IDS, type TemplateId } from "../../../convex/templateTypes";
import {
  TEMPLATE_CATALOG,
  type TemplateCatalogEntry,
} from "../../lib/templateCatalog";
import {
  clearBuilderFlowState,
  clearLegacyPendingSiteState,
} from "../../lib/builderFlowStorage";
import {
  DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID,
  getTemplatePreviewPalettes,
  getTemplatePreviewStyleOverrides,
  isTemplatePreviewPaletteId,
  type TemplatePreviewPalette,
  type TemplatePreviewPaletteId,
} from "../../lib/templatePalettes";
import { buildTemplatePreviewHtml } from "../../lib/templatePreview";

export const Route = createFileRoute("/templates/$templateId")({
  component: TemplatePreviewPage,
  validateSearch: (search: Record<string, unknown>) => ({
    ...(search.from === "create" ? { from: "create" as const } : {}),
    ...(typeof search.palette === "string" &&
    isTemplatePreviewPaletteId(search.palette)
      ? { palette: search.palette }
      : {}),
  }),
});

function TemplatePreviewPage() {
  const { templateId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const isValidTemplate = (TEMPLATE_IDS as readonly string[]).includes(
    templateId,
  );
  const catalogIndex = TEMPLATE_CATALOG.findIndex(
    (entry) => entry.id === templateId,
  );
  const catalogEntry = TEMPLATE_CATALOG[catalogIndex];
  const prevTemplate =
    TEMPLATE_CATALOG[
      (catalogIndex - 1 + TEMPLATE_CATALOG.length) % TEMPLATE_CATALOG.length
    ];
  const nextTemplate =
    TEMPLATE_CATALOG[(catalogIndex + 1) % TEMPLATE_CATALOG.length];
  const selectedPaletteId =
    search.palette ?? DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID;
  const fromCreate = search.from === "create";

  if (!isValidTemplate) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[var(--color-page)] px-4 text-center text-[var(--color-ink)]">
        <h1 className="font-display text-4xl">
          <Trans>Plantilla no encontrada</Trans>
        </h1>
        <Link
          className="app-btn-primary cursor-pointer px-6 py-3"
          hash="gallery"
          to="/"
        >
          <Trans>Ver todas las plantillas</Trans>
        </Link>
      </div>
    );
  }

  const selectTemplate = () => {
    if (fromCreate) {
      clearLegacyPendingSiteState();
    } else {
      clearBuilderFlowState();
    }
    void navigate({
      search: {
        palette:
          selectedPaletteId === DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID
            ? undefined
            : selectedPaletteId,
        step: "details",
        template: templateId as TemplateId,
      },
      to: "/create",
    });
  };

  const handleSelectPalette = (id: TemplatePreviewPaletteId) => {
    void navigate({
      params: { templateId },
      replace: true,
      search: {
        ...(fromCreate ? { from: "create" as const } : {}),
        palette: id === DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID ? undefined : id,
      },
      to: "/templates/$templateId",
    });
  };

  const palettes = getTemplatePreviewPalettes(templateId as TemplateId);

  return (
    <div className="flex h-screen flex-col bg-[var(--color-page)] text-[var(--color-ink)] [@supports(height:100dvh)]:h-dvh">
      <nav className="relative z-30 flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3 sm:px-6">
        {fromCreate ? (
          <Link
            className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
            data-testid="template-preview-back"
            search={{ step: undefined, template: undefined }}
            to="/create"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">
              <Trans>Ver todas</Trans>
            </span>
          </Link>
        ) : (
          <Link
            className="flex cursor-pointer items-center gap-2 text-sm text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
            data-testid="template-preview-back"
            hash="gallery"
            to="/"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">
              <Trans>Ver todas</Trans>
            </span>
          </Link>
        )}

        <div className="flex items-center gap-1 sm:gap-2">
          <Link
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center  text-[var(--color-muted)] transition-colors hover:bg-[var(--color-page-soft)] hover:text-[var(--color-ink)]"
            params={{ templateId: prevTemplate.id }}
            search={fromCreate ? { from: "create" } : { palette: undefined }}
            to="/templates/$templateId"
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <h1 className="font-display text-lg text-[var(--color-ink)] sm:text-2xl">
            <span className="sm:hidden">
              {catalogEntry?.label.split(" / ")[0]}
            </span>
            <span className="hidden sm:inline">{catalogEntry?.label}</span>
          </h1>
          <Link
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center  text-[var(--color-muted)] transition-colors hover:bg-[var(--color-page-soft)] hover:text-[var(--color-ink)]"
            params={{ templateId: nextTemplate.id }}
            search={fromCreate ? { from: "create" } : { palette: undefined }}
            to="/templates/$templateId"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <button
            aria-expanded={paletteOpen}
            aria-label={t`Cambiar paleta de colores`}
            className="hidden h-10 w-10 cursor-pointer items-center justify-center border border-[var(--color-border)] text-[var(--color-accent)] transition-colors hover:bg-[var(--color-page-soft)] sm:inline-flex"
            data-testid="landing-desktop-palette-toggle"
            onClick={() => setPaletteOpen((open) => !open)}
            type="button"
          >
            {paletteOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Palette className="h-5 w-5" />
            )}
          </button>

          <button
            className="app-btn-primary cursor-pointer px-4 py-2 text-sm"
            data-testid="landing-select-template"
            onClick={selectTemplate}
            type="button"
          >
            <Trans>Seleccionar</Trans>
          </button>
        </div>
      </nav>

      <TemplatePreview
        catalogEntry={catalogEntry}
        key={`${templateId}-${selectedPaletteId}`}
        onSelectPalette={handleSelectPalette}
        paletteId={selectedPaletteId}
        paletteOpen={paletteOpen}
        palettes={palettes}
        setPaletteOpen={setPaletteOpen}
        templateId={templateId as TemplateId}
      />
    </div>
  );
}

function TemplatePreview({
  catalogEntry,
  onSelectPalette,
  paletteId,
  paletteOpen,
  palettes,
  setPaletteOpen,
  templateId,
}: {
  catalogEntry: TemplateCatalogEntry;
  onSelectPalette: (paletteId: TemplatePreviewPaletteId) => void;
  paletteId: TemplatePreviewPaletteId;
  paletteOpen: boolean;
  palettes: Array<TemplatePreviewPalette>;
  setPaletteOpen: Dispatch<SetStateAction<boolean>>;
  templateId: TemplateId;
}) {
  const [previewMarkup, setPreviewMarkup] = useState("");
  const [previewLoaded, setPreviewLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const styleOverrides = getTemplatePreviewStyleOverrides(
      templateId,
      paletteId,
    );

    void buildTemplatePreviewHtml(templateId, styleOverrides)
      .then((markup) => {
        if (!cancelled) {
          setPreviewMarkup(markup);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewMarkup("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, paletteId]);

  return (
    <div
      className="relative flex-1 overflow-hidden"
      data-testid="landing-template-preview"
    >
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center bg-[var(--color-page)] transition-opacity duration-700 ease-out ${
          previewLoaded ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <div className="space-y-4 text-center text-[var(--color-muted)]">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
          <div className="space-y-1">
            <p className="font-medium text-[var(--color-ink)]">
              <Trans>Preparando la plantilla a tamano real</Trans>
            </p>
            <p className="text-sm">
              <Trans>
                Cargando tipografias, secciones y detalles de muestra.
              </Trans>
            </p>
          </div>
        </div>
      </div>

      {previewMarkup && (
        <iframe
          className={`h-full w-full transition-opacity duration-700 ease-out ${
            previewLoaded ? "opacity-100" : "opacity-0"
          }`}
          data-testid="landing-template-preview-iframe"
          onLoad={() => setPreviewLoaded(true)}
          sandbox="allow-scripts"
          srcDoc={previewMarkup}
          title={`Vista previa de ${catalogEntry.label}`}
        />
      )}

      <div
        className={`absolute top-3 right-6 z-20 hidden items-center gap-2 sm:flex ${
          paletteOpen ? "" : "pointer-events-none"
        }`}
        data-testid="landing-template-palette-picker"
      >
        {palettes.map((palette, index) => {
          const isSelected = palette.id === paletteId;

          return (
            <button
              aria-label={palette.label}
              aria-pressed={isSelected}
              className={`flex h-12 w-12 cursor-pointer items-center justify-center border bg-white shadow-sm transition-all duration-200 ease-out ${
                paletteOpen
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-2 opacity-0"
              } ${
                isSelected
                  ? "border-[var(--color-ink)] ring-1 ring-[var(--color-ink)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-ink)]"
              }`}
              data-testid={`landing-template-palette-${palette.id}`}
              key={palette.id}
              onClick={() => {
                onSelectPalette(palette.id);
                setPaletteOpen(false);
              }}
              style={{
                transitionDelay: paletteOpen ? `${index * 40}ms` : "0ms",
              }}
              tabIndex={paletteOpen ? 0 : -1}
              title={palette.label}
              type="button"
            >
              <span className="flex -space-x-1">
                {[
                  palette.colors.colorBackground,
                  palette.colors.colorPrimary,
                  palette.colors.colorSecondary,
                ].map((color, dotIndex) => (
                  <span
                    className="h-4 w-4 rounded-full border border-white/80 shadow-sm"
                    key={`${palette.id}-${dotIndex}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <div className="absolute right-8 bottom-[calc(2rem+env(safe-area-inset-bottom))] z-20 sm:hidden">
        <div className="relative" data-testid="landing-mobile-palette-picker">
          <div
            className={`absolute right-0 bottom-full mb-3 flex flex-col-reverse items-end gap-3 ${
              paletteOpen ? "" : "pointer-events-none"
            }`}
          >
            {palettes.map((palette, index) => {
              const isSelected = palette.id === paletteId;

              return (
                <button
                  aria-label={palette.label}
                  aria-pressed={isSelected}
                  className={`flex h-12 w-12 cursor-pointer items-center justify-center border bg-white shadow-sm transition-all duration-200 ease-out ${
                    paletteOpen
                      ? "translate-y-0 opacity-100"
                      : "translate-y-2 opacity-0"
                  } ${
                    isSelected
                      ? "border-[var(--color-ink)] ring-1 ring-[var(--color-ink)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-ink)]"
                  }`}
                  data-testid={`landing-mobile-palette-${palette.id}`}
                  key={palette.id}
                  onClick={() => {
                    onSelectPalette(palette.id);
                    setPaletteOpen(false);
                  }}
                  style={{
                    transitionDelay: paletteOpen ? `${index * 40}ms` : "0ms",
                  }}
                  tabIndex={paletteOpen ? 0 : -1}
                  title={palette.label}
                  type="button"
                >
                  <span className="flex -space-x-1">
                    {[
                      palette.colors.colorBackground,
                      palette.colors.colorPrimary,
                      palette.colors.colorSecondary,
                    ].map((color, dotIndex) => (
                      <span
                        className="h-4 w-4 rounded-full border border-white/80 shadow-sm"
                        key={`${palette.id}-${dotIndex}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            aria-expanded={paletteOpen}
            aria-label={t`Cambiar paleta de colores`}
            className="flex h-12 w-12 cursor-pointer items-center justify-center border border-[var(--color-border)] bg-white text-[var(--color-accent)] shadow-sm transition-colors hover:bg-[var(--color-page-soft)]"
            data-testid="landing-mobile-palette-toggle"
            onClick={() => setPaletteOpen((open) => !open)}
            type="button"
          >
            {paletteOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Palette className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
